import type { RecentlyUpdatedMacroQuery$variables } from '../__generated__/RecentlyUpdatedMacroQuery.graphql.ts';

import type { ContentType, RecentlyUpdatedMacroParams } from './recently-updated-types.ts';

export const DEFAULT_MAX = 15;

export const DEFAULT_TYPES: ContentType[] = [
	'page',
	'blogpost',
	'comment',
	'attachment',
	'whiteboard',
	'database',
	'embed',
	'folder',
];

const TYPE_ALIASES: Record<string, ContentType> = {
	news: 'blogpost',
	blog: 'blogpost',
	blogs: 'blogpost',
	'blog-post': 'blogpost',
	blogposts: 'blogpost',
	'blog-posts': 'blogpost',
	pages: 'page',
	comments: 'comment',
	attachments: 'attachment',
};

const SUPPORTED_TYPES = new Set(DEFAULT_TYPES);

function isContentType(type: string): type is ContentType {
	return SUPPORTED_TYPES.has(type as ContentType);
}

function normalizeType(rawType: string): ContentType | undefined {
	const normalized = TYPE_ALIASES[rawType] ?? rawType;
	return isContentType(normalized) ? normalized : undefined;
}

function parseCommaSeparatedValues(value: string | undefined): string[] {
	if (!value) {
		return [];
	}
	const parts = value.split(',');
	const result: string[] = [];
	for (const part of parts) {
		const trimmed = part.trim();
		if (trimmed.length > 0) {
			result.push(trimmed);
		}
	}
	return result;
}

const SPACE_SCOPE_TOKENS: Record<string, string> = {
	'@personal': 'space.type = personal',
	'@global': 'space.type != personal',
};

function resolveSpaceClause(
	spaces: string | undefined,
	currentSpaceKey: string,
): string | undefined {
	const configuredSpaceKeys = parseCommaSeparatedValues(spaces);
	if (configuredSpaceKeys.length === 0) {
		return currentSpaceKey ? `space = "${currentSpaceKey}"` : undefined;
	}
	if (configuredSpaceKeys.includes('@all') || configuredSpaceKeys.includes('*')) {
		return undefined;
	}

	const resolvedSpaceKeys: string[] = [];
	const spaceScopeClauses = new Set<string>();
	let includeFavoriteSpaces = false;
	for (const spaceKey of configuredSpaceKeys) {
		if (spaceKey === '@favorite' || spaceKey === '@favourite') {
			includeFavoriteSpaces = true;
			continue;
		}
		if (SPACE_SCOPE_TOKENS[spaceKey]) {
			spaceScopeClauses.add(SPACE_SCOPE_TOKENS[spaceKey]);
			continue;
		}

		const resolvedSpaceKey = spaceKey === '@self' ? currentSpaceKey : spaceKey;
		if (resolvedSpaceKey) {
			resolvedSpaceKeys.push(resolvedSpaceKey);
		}
	}

	const clauses: string[] = [];
	if (resolvedSpaceKeys.length === 1) {
		clauses.push(`space = "${resolvedSpaceKeys[0]}"`);
	} else if (resolvedSpaceKeys.length > 1) {
		clauses.push(`space in (${formatQuotedCqlValues(resolvedSpaceKeys)})`);
	}
	for (const spaceScopeClause of spaceScopeClauses) {
		clauses.push(spaceScopeClause);
	}
	if (includeFavoriteSpaces) {
		clauses.push('space in (favouriteSpaces())');
	}

	return clauses.length > 1 ? `(${clauses.join(' OR ')})` : clauses[0];
}

function formatQuotedCqlValues(values: string[]): string {
	return values.map((v) => `"${v}"`).join(', ');
}

export function resolveMaxItems(max: RecentlyUpdatedMacroParams['max']): number {
	const parsed = Number(max);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX;
}

/**
 * Builds a CQL query string from the macro params, matching the behaviour of the
 * legacy RecentUpdateQueryParameters Java class which accepts:
 *   - followingUsers  (author param — comma-separated account IDs)
 *   - labels          (comma-separated label values)
 *   - spaceKeys       (comma-separated space keys)
 *   - contentTypes    (comma-separated types: page, blogpost, comment, attachment)
 *
 * @param params - Resolved macro parameters with string values.
 * @param spaceClause - The resolved CQL space clause.
 */
export function buildCql(params: RecentlyUpdatedMacroParams, spaceClause?: string): string {
	const clauses: string[] = [];

	// Content types
	const typeSet = new Set<ContentType>();
	if (params.types) {
		for (const part of parseCommaSeparatedValues(String(params.types))) {
			const normalizedType = normalizeType(part.toLowerCase());
			if (normalizedType) {
				typeSet.add(normalizedType);
			}
		}
	} else {
		for (const t of DEFAULT_TYPES) {
			typeSet.add(t);
		}
	}
	if (typeSet.size > 0) {
		clauses.push(`type in (${[...typeSet].join(', ')})`);
	}

	// Space scope — defaults to the current space
	if (spaceClause) {
		clauses.push(spaceClause);
	}

	// Labels
	if (params.labels) {
		const labelList = String(params.labels)
			.split(',')
			.map((l) => `"${l.trim()}"`)
			.join(', ');
		clauses.push(`label in (${labelList})`);
	}

	// Following users / contributors (author param — comma-separated account IDs)
	if (params.author) {
		const users = String(params.author)
			.split(',')
			.map((u) => `"${u.trim()}"`)
			.join(', ');
		clauses.push(`contributor in (${users})`);
	}

	return `${clauses.join(' AND ')} order by lastmodified desc`;
}

export function buildRecentlyUpdatedQueryVariables(
	params: RecentlyUpdatedMacroParams,
	currentSpaceKey: string,
	after: string = '',
): RecentlyUpdatedMacroQuery$variables {
	const maxItems = resolveMaxItems(params.max);
	const spaceClause = resolveSpaceClause(params.spaces, currentSpaceKey);

	return {
		cql: buildCql(params, spaceClause),
		cqlcontext: '{"contentStatuses":["draft", "current"]}',
		first: maxItems,
		after,
	};
}
